import { Rng, hash2, hashString } from '../../core/rng';
import type { AccountSave, MarketListing } from '../../game/account';
import { COMBAT_SIGILS } from '../../game/build';
import type { CharacterSave } from '../../game/character';
import { countOf, removeStack } from '../../game/inventory';
import { ITEMS } from '../../game/items';
import type { Progression } from '../../game/Progression';
import { roomFor } from './bag';

/**
 * The Havenreach market: an NPC-driven order book for tradeable stackables,
 * priced in gold per bundle. Prices are a pure function of (item, day) so
 * every peer and every test sees the same numbers; NPC sell orders are
 * re-stocked each day into `account.market.listings`. Player listings are
 * consignments that sell after a simulated delay; proceeds (minus a 5% fee)
 * arrive by mail.
 */

export interface TradeGood {
  id: string;
  /** Units per bundle (prices are per bundle). */
  bundle: number;
  /** Base mid price in gold per bundle. */
  base: number;
  cat: string;
  sub: string;
}

export interface MarketCategory {
  id: string;
  name: string;
  subs: { id: string; name: string }[];
}

export const MARKET_CATEGORIES: MarketCategory[] = [
  {
    id: 'temper',
    name: 'Tempering',
    subs: [
      { id: 'shards', name: 'Shards & ore' },
      { id: 'leap', name: 'Leap & alloy' },
      { id: 'rate', name: 'Catalysts' },
    ],
  },
  {
    id: 'life',
    name: 'Life-skill materials',
    subs: [
      { id: 'foraging', name: 'Foraging' },
      { id: 'logging', name: 'Logging' },
      { id: 'mining', name: 'Mining' },
      { id: 'fishing', name: 'Fishing' },
      { id: 'excavating', name: 'Excavating' },
      { id: 'hunting', name: 'Hunting' },
    ],
  },
  {
    id: 'battle',
    name: 'Battle items',
    subs: [
      { id: 'recovery', name: 'Recovery' },
      { id: 'offense', name: 'Grenades & bombs' },
      { id: 'utility', name: 'Utility' },
    ],
  },
  {
    id: 'gems',
    name: 'Gems',
    subs: [
      { id: 'ember', name: 'Ember (damage)' },
      { id: 'tide', name: 'Tide (cooldown)' },
    ],
  },
  { id: 'codex', name: 'Codices', subs: [{ id: 'sigil', name: 'Sigil codices' }] },
];

export const GOODS: Record<string, TradeGood> = {};
const good = (id: string, bundle: number, base: number, cat: string, sub: string) => {
  GOODS[id] = { id, bundle, base, cat, sub };
};

good('aether_shard', 500, 14, 'temper', 'shards');
good('ore_weapon', 50, 11, 'temper', 'shards');
good('ore_armor', 50, 7, 'temper', 'shards');
good('leap_crystal', 5, 9, 'temper', 'leap');
good('fusion_alloy', 5, 17, 'temper', 'leap');
good('catalyst', 1, 8, 'temper', 'rate');

good('herb', 50, 7, 'life', 'foraging');
good('herb_rare', 5, 12, 'life', 'foraging');
good('timber', 50, 7, 'life', 'logging');
good('timber_rare', 5, 12, 'life', 'logging');
good('ore_raw', 50, 8, 'life', 'mining');
good('ore_rare', 5, 15, 'life', 'mining');
good('fish', 25, 8, 'life', 'fishing');
good('fish_rare', 5, 19, 'life', 'fishing');
good('relic_fragment', 25, 9, 'life', 'excavating');
good('relic_rare', 1, 8, 'life', 'excavating');
good('hide', 50, 7, 'life', 'hunting');

good('potion_small', 10, 5, 'battle', 'recovery');
good('potion_major', 5, 7, 'battle', 'recovery');
good('potion_elixir', 3, 10, 'battle', 'recovery');
good('potion_panacea', 3, 5, 'battle', 'recovery');
for (const id of [
  'grenade_flame',
  'grenade_frost',
  'grenade_flash',
  'grenade_clay',
  'bomb_corrosive',
  'bomb_sleep',
])
  good(id, 3, 6, 'battle', 'offense');
good('grenade_whirl', 3, 9, 'battle', 'offense');
good('grenade_dark', 3, 8, 'battle', 'offense');
good('bomb_destruction', 3, 10, 'battle', 'offense');
good('potion_protect', 3, 6, 'battle', 'utility');
good('potion_timestop', 1, 6, 'battle', 'utility');
good('flag_march', 3, 5, 'battle', 'utility');
good('campfire_kit', 3, 5, 'battle', 'utility');

const GEM_BASE = [0, 6, 18, 55, 160, 480];
for (let lvl = 1; lvl <= 5; lvl++) {
  good(`gem_dmg_${lvl}`, 1, GEM_BASE[lvl] ?? 6, 'gems', 'ember');
  good(`gem_cd_${lvl}`, 1, Math.round((GEM_BASE[lvl] ?? 6) * 0.85), 'gems', 'tide');
}

/** Codex prices follow how sought-after each sigil is. */
const CODEX_BASE: Record<string, number> = {
  vendetta: 95,
  hexed_idol: 110,
  brutal_edge: 60,
  battle_fervor: 45,
  steady_hand: 70,
  ambusher: 50,
  brawler: 50,
  quickening: 35,
  overcharge: 40,
  bulwark: 30,
  precision: 65,
  apex_mastery: 25,
  ironhide: 15,
  pressure_points: 20,
  composure: 55,
};
for (const sigil of COMBAT_SIGILS)
  if (ITEMS[`book_${sigil}`]) good(`book_${sigil}`, 1, CODEX_BASE[sigil] ?? 40, 'codex', 'sigil');

export const isTradeable = (id: string): boolean => id in GOODS && id in ITEMS;

// ---------------------------------------------------------------- pricing

/** Maximum daily swing around the base price. */
export const PRICE_BAND = 0.14;
/** NPC sell orders sit at these multiples of the mid price. */
export const ASK_MULTS = [1.03, 1.08, 1.15] as const;
/** NPC buy orders (instant sell) pay this multiple of the mid price. */
export const BID_MULT = 0.9;
export const FEE_RATE = 0.05;
export const MAX_PLAYER_LISTINGS = 10;
/** Buyers accept consignments priced up to this multiple of the mid. */
export const SELL_CEILING = 1.25;

const n1 = (key: number, i: number): number => hash2(i, 7, key) * 2 - 1;
function smooth(key: number, x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const t = f * f * (3 - 2 * f);
  const a = n1(key, i);
  return a + (n1(key, i + 1) - a) * t;
}

/** Deterministic price multiplier in [1 - PRICE_BAND, 1 + PRICE_BAND]. */
export function priceMult(item: string, day: number): number {
  const key = hashString(item) | 0;
  const cat = hashString(GOODS[item]?.cat ?? item) | 0;
  // A slow 4-day swell, a daily jitter and a weekly category-wide drift.
  const v =
    0.55 * smooth(key, day / 4) + 0.25 * n1(key ^ 0x5bd1e995, day) + 0.2 * smooth(cat, day / 7);
  return 1 + PRICE_BAND * Math.max(-1, Math.min(1, v));
}

export function midPrice(item: string, day: number): number {
  const g = GOODS[item];
  if (!g) return 0;
  return Math.max(1, Math.round(g.base * priceMult(item, day)));
}

export const bidPrice = (item: string, day: number): number =>
  Math.max(1, Math.floor(midPrice(item, day) * BID_MULT));
export const askPrices = (item: string, day: number): number[] =>
  ASK_MULTS.map((m) => Math.max(1, Math.ceil(midPrice(item, day) * m)));

/** Unrounded mid prices for charts (`n` days ending at `day`, oldest first). */
export function priceSeries(item: string, day: number, n = 14): number[] {
  const base = GOODS[item]?.base ?? 0;
  return Array.from({ length: n }, (_, i) => base * priceMult(item, day - (n - 1) + i));
}

/** Mid prices for the `n` days ending at `day` (oldest first). */
export function priceHistory(item: string, day: number, n = 14): number[] {
  return Array.from({ length: n }, (_, i) => midPrice(item, day - (n - 1) + i));
}

export function marketFee(gross: number): number {
  return gross > 0 ? Math.ceil(gross * FEE_RATE) : 0;
}

export function listingPayout(
  price: number,
  bundles: number,
): { gross: number; fee: number; net: number } {
  const gross = price * bundles;
  const fee = marketFee(gross);
  return { gross, fee, net: gross - fee };
}

/** Seconds until a consignment finds a buyer: cheap listings go fast, greedy ones crawl. */
export function sellDelaySec(price: number, mid: number): number {
  const ratio = price / Math.max(1, mid);
  return Math.round(Math.min(900, Math.max(15, 45 * Math.exp((ratio - 1) * 9))));
}

export const priceBounds = (item: string, day: number): [number, number] => {
  const m = midPrice(item, day);
  return [Math.max(1, Math.ceil(m * 0.5)), Math.max(2, Math.floor(m * 2))];
};

// ---------------------------------------------------------------- order book

export interface PlayerListing extends MarketListing {
  listedAt: number;
  sellsAt: number;
}

export const isPlayerListing = (l: MarketListing): l is PlayerListing =>
  l.player && typeof (l as Partial<PlayerListing>).sellsAt === 'number';

const NPC_SELLERS = [
  'Lantern Trade Guild',
  'Saltwind Traders',
  'Emberforge Consortium',
  'Brisk & Daughters',
  'Duneway Caravans',
  'Rimebound Provisioners',
  'Whisperwood Collective',
  'Shardlight Exchange',
];

/** NPC stock (bundles) per ask level: deeper at the pricier levels, thin for expensive goods. */
const SUPPLY: readonly (readonly [number, number])[] = [
  [3, 12],
  [5, 20],
  [8, 30],
];

function supply(g: TradeGood, level: number, rng: Rng): number {
  const scarce = g.base >= 100 ? 0.25 : g.base >= 40 ? 0.5 : 1;
  const [lo, hi] = SUPPLY[level] ?? [3, 12];
  return Math.max(1, Math.round(rng.int(lo, hi) * scarce));
}

/** Restocks the NPC sell orders for the account's current day (no-op if already done). */
export function refreshMarket(a: AccountSave): boolean {
  if (a.market.lastRefresh === a.day) return false;
  a.market.listings = a.market.listings.filter((l) => l.player);
  for (const g of Object.values(GOODS)) {
    if (!ITEMS[g.id]) continue;
    const rng = new Rng(`market:${g.id}:${a.day}`);
    askPrices(g.id, a.day).forEach((price, i) => {
      a.market.listings.push({
        id: `npc.${g.id}.${i}`,
        item: g.id,
        count: supply(g, i, rng),
        price,
        seller: rng.pick(NPC_SELLERS),
        player: false,
      });
    });
  }
  a.market.lastRefresh = a.day;
  return true;
}

/** Sell orders for an item, cheapest first (the player's own listings are shown but not buyable). */
export function orderBook(a: AccountSave, item: string): MarketListing[] {
  return a.market.listings
    .filter((l) => l.item === item && l.count > 0)
    .sort((x, y) => x.price - y.price || Number(x.player) - Number(y.player));
}

export function playerListings(a: AccountSave): PlayerListing[] {
  return a.market.listings.filter(isPlayerListing);
}

export interface BuyQuote {
  cost: number;
  filled: number;
  /** Units received (`filled * bundle`). */
  units: number;
  fills: { id: string; take: number; price: number }[];
}

export function quoteBuy(a: AccountSave, item: string, bundles: number): BuyQuote {
  const g = GOODS[item];
  const q: BuyQuote = { cost: 0, filled: 0, units: 0, fills: [] };
  if (!g || bundles <= 0) return q;
  for (const l of orderBook(a, item)) {
    if (l.player || q.filled >= bundles) continue;
    const take = Math.min(l.count, bundles - q.filled);
    q.fills.push({ id: l.id, take, price: l.price });
    q.filled += take;
    q.cost += take * l.price;
  }
  q.units = q.filled * g.bundle;
  return q;
}

export type MarketResult =
  { ok: true; gold: number; units: number } | { ok: false; reason: string };

const bumpCounter = (c: CharacterSave, k: string, n = 1) => {
  c.counters[k] = (c.counters[k] ?? 0) + n;
};

export function buyFromMarket(p: Progression, item: string, bundles: number): MarketResult {
  const a = p.account;
  const g = GOODS[item];
  if (!g || bundles < 1) return { ok: false, reason: 'Nothing to buy' };
  const q = quoteBuy(a, item, bundles);
  if (q.filled < bundles) return { ok: false, reason: 'Not enough supply today' };
  if (a.wallet.gold < q.cost) return { ok: false, reason: 'Not enough gold' };
  if (roomFor(p.char, item) < q.units) return { ok: false, reason: 'Bag is full' };
  a.wallet.gold -= q.cost;
  for (const f of q.fills) {
    const l = a.market.listings.find((x) => x.id === f.id);
    if (l) l.count -= f.take;
  }
  a.market.listings = a.market.listings.filter((l) => l.player || l.count > 0);
  p.giveItem(item, q.units);
  bumpCounter(p.char, 'marketBuys');
  p.emit('wallet', { silver: a.wallet.silver, gold: a.wallet.gold });
  return { ok: true, gold: -q.cost, units: q.units };
}

/** Instant sale to the NPC buy orders at the bid price. */
export function sellToMarket(p: Progression, item: string, bundles: number): MarketResult {
  const g = GOODS[item];
  if (!g || bundles < 1) return { ok: false, reason: 'Nothing to sell' };
  const units = bundles * g.bundle;
  if (countOf(p.char, item) < units)
    return {
      ok: false,
      reason: `You need ${units} to sell ${bundles} bundle${bundles > 1 ? 's' : ''}`,
    };
  removeStack(p.char, item, units);
  const gold = bidPrice(item, p.account.day) * bundles;
  p.account.wallet.gold += gold;
  bumpCounter(p.char, 'marketSales');
  p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  return { ok: true, gold, units };
}

/** Puts bundles up for sale at `price` gold per bundle; items are held in escrow. */
export function listOnMarket(
  p: Progression,
  item: string,
  bundles: number,
  price: number,
  now = Date.now(),
): { ok: true; listing: PlayerListing } | { ok: false; reason: string } {
  const a = p.account;
  const g = GOODS[item];
  if (!g || bundles < 1) return { ok: false, reason: 'Nothing to list' };
  if (playerListings(a).length >= MAX_PLAYER_LISTINGS)
    return { ok: false, reason: `At most ${MAX_PLAYER_LISTINGS} listings at once` };
  const units = bundles * g.bundle;
  if (countOf(p.char, item) < units)
    return {
      ok: false,
      reason: `You need ${units} to list ${bundles} bundle${bundles > 1 ? 's' : ''}`,
    };
  const [lo, hi] = priceBounds(item, a.day);
  const unit = Math.round(Math.min(hi, Math.max(lo, price)));
  removeStack(p.char, item, units);
  const listing: PlayerListing = {
    id: `pl_${now.toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`,
    item,
    count: bundles,
    price: unit,
    seller: p.char.name,
    player: true,
    listedAt: now,
    sellsAt: now + sellDelaySec(unit, midPrice(item, a.day)) * 1000,
  };
  a.market.listings.push(listing);
  return { ok: true, listing };
}

/** Withdraws a listing; the items come back (overflow goes to mail). */
export function cancelListing(p: Progression, id: string): boolean {
  const a = p.account;
  const l = playerListings(a).find((x) => x.id === id);
  if (!l) return false;
  a.market.listings = a.market.listings.filter((x) => x.id !== id);
  const g = GOODS[l.item];
  p.giveItem(l.item, l.count * (g?.bundle ?? 1));
  return true;
}

export interface Sale {
  listing: PlayerListing;
  gross: number;
  fee: number;
  net: number;
}

/** Settles consignments whose buyer has arrived; proceeds are mailed. */
export function processListings(a: AccountSave, now = Date.now()): Sale[] {
  const sold: Sale[] = [];
  for (const l of playerListings(a)) {
    if (now < l.sellsAt) continue;
    if (l.price > Math.ceil(midPrice(l.item, a.day) * SELL_CEILING)) continue;
    const pay = listingPayout(l.price, l.count);
    sold.push({ listing: l, ...pay });
    const g = GOODS[l.item];
    const units = l.count * (g?.bundle ?? 1);
    const name = ITEMS[l.item]?.name ?? l.item;
    a.mail.unshift({
      id: `m_${now.toString(36)}_${l.id}`,
      from: 'Venn · Market Broker',
      subject: `Sold: ${name} ×${units}`,
      body: `${l.count} bundle${l.count > 1 ? 's' : ''} at ${l.price} gold. Proceeds ${pay.gross} gold, broker's fee ${pay.fee} gold (5%).`,
      items: [],
      silver: 0,
      gold: pay.net,
      read: false,
      at: now,
    });
  }
  if (sold.length) {
    const ids = new Set(sold.map((s) => s.listing.id));
    a.market.listings = a.market.listings.filter((l) => !ids.has(l.id));
  }
  return sold;
}

/** Why a listing hasn't sold yet (for the My Listings tab). */
export function listingStatus(
  l: PlayerListing,
  day: number,
  now = Date.now(),
): { waiting: boolean; text: string } {
  const cap = Math.ceil(midPrice(l.item, day) * SELL_CEILING);
  if (l.price > cap) return { waiting: true, text: 'Priced above what buyers will pay today' };
  const left = Math.max(0, Math.ceil((l.sellsAt - now) / 1000));
  return {
    waiting: false,
    text:
      left > 0
        ? `Buyer expected in ${left >= 60 ? `${Math.ceil(left / 60)} min` : `${left} s`}`
        : 'Settling…',
  };
}

export function searchGoods(query: string, cat: string | null, sub: string | null): TradeGood[] {
  const q = query.trim().toLowerCase();
  return Object.values(GOODS).filter((g) => {
    if (!ITEMS[g.id]) return false;
    if (q) return (ITEMS[g.id]?.name ?? g.id).toLowerCase().includes(q);
    if (cat && g.cat !== cat) return false;
    if (sub && g.sub !== sub) return false;
    return true;
  });
}
